import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  CircularProgress,
  Alert,
  LinearProgress,
  Divider,
  Stack,
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import SchoolDashboardService from '../services/schoolDashboard.service';

const PRIMARY = '#5248ee';
const PRIMARY_LIGHT = '#f0efff';

const KPI_CONFIGS = [
  {
    key: 'totalStudents',
    label: 'Total Alumnos',
    icon: '👥',
    color: '#3b82f6',
    iconBg: '#e9eefe',
    getValue: (d) => d?.totalStudents?.current ?? 0,
    getSubtext: (d) => `+${d?.totalStudents?.newThisMonth ?? 0} este mes`,
  },
  {
    key: 'attendancePct',
    label: 'Asistencia Hoy',
    icon: '✅',
    color: '#22b14c',
    iconBg: '#e5f8e9',
    getValue: (d) => `${d?.attendanceToday?.percentage ?? 0}%`,
    getSubtext: (d) => `${d?.attendanceToday?.present ?? 0} presentes`,
  },
  {
    key: 'absent',
    label: 'Ausentes Hoy',
    icon: '❌',
    color: '#ee4444',
    iconBg: '#fee7e7',
    getValue: (d) => d?.attendanceToday?.absent ?? 0,
    getSubtext: (d) => {
      const total = d?.attendanceToday?.total ?? 0;
      const absent = d?.attendanceToday?.absent ?? 0;
      return total > 0 ? `${((absent / total) * 100).toFixed(1)}% del total` : '0% del total';
    },
  },
  {
    key: 'late',
    label: 'Tardanzas Hoy',
    icon: '⏰',
    color: '#f49e12',
    iconBg: '#fff4de',
    getValue: (d) => d?.attendanceToday?.late ?? 0,
    getSubtext: (d) => {
      const total = d?.attendanceToday?.total ?? 0;
      const late = d?.attendanceToday?.late ?? 0;
      return total > 0 ? `${((late / total) * 100).toFixed(1)}% del total` : '0% del total';
    },
  },
];

const GRADE_COLORS = ['#5248ee', '#3b82f6', '#22b14c', '#f49e12', '#ee4444', '#9966e5'];

const STATUS_STYLES = {
  pending:   { bg: '#fff4de', color: '#f49e12', label: 'Pendiente' },
  completed: { bg: '#e5f8e9', color: '#22b14c', label: 'Completada' },
  draft:     { bg: '#e9eefe', color: '#3b82f6', label: 'Borrador' },
};

const ACTIVITY_COLORS = {
  attendance:    '#22b14c',
  student:       '#3b82f6',
  planification: PRIMARY,
  default:       '#f49e12',
};

function formatPlanDate(dateStr) {
  if (!dateStr) return '';
  const today = new Date();
  const d = new Date(dateStr);
  const diff = Math.round((d - today) / 86400000);
  if (diff === 0) return 'Hoy';
  if (diff === 1) return 'Mañana';
  const days = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
  return days[d.getDay()];
}

export default function SchoolDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const today = new Date().toLocaleDateString('es-GT', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  });

  useEffect(() => {
    SchoolDashboardService.getStats()
      .then((res) => {
        if (res.data.success) setData(res.data.data);
        else setError('No se pudo cargar el dashboard');
      })
      .catch(() => setError('Error al conectar con el servidor'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error" sx={{ m: 3 }}>{error}</Alert>;
  }

  const gradeColors = {};
  (data?.studentsPerGrade ?? []).forEach((g, i) => {
    gradeColors[g.grade] = GRADE_COLORS[i % GRADE_COLORS.length];
  });

  const maxStudents = Math.max(...(data?.studentsPerGrade ?? []).map((g) => g.count), 1);

  return (
    <Box sx={{ bgcolor: '#f5f5f8', minHeight: '100vh', p: 3 }}>
      {/* Header */}
      <Typography variant="h5" fontWeight={700} color="#1c1c1e">
        ¡Bienvenido, {user?.name ?? 'Admin'}!
      </Typography>
      <Typography variant="body2" color="#6d6d77" mt={0.5} mb={2}>
        Aquí está el resumen de hoy — {data?.cycle?.name ?? today}
      </Typography>

      {/* Quick actions */}
      <Stack direction="row" spacing={1.5} flexWrap="wrap" mb={3}>
        <Button
          variant="contained"
          size="small"
          onClick={() => navigate('/attendance')}
          sx={{ bgcolor: PRIMARY, '&:hover': { bgcolor: '#3f35d4' }, textTransform: 'none', fontWeight: 600 }}
        >
          + Tomar Asistencia
        </Button>
        <Button
          variant="contained"
          size="small"
          onClick={() => navigate('/students/create')}
          sx={{ bgcolor: PRIMARY_LIGHT, color: PRIMARY, boxShadow: 'none', '&:hover': { bgcolor: '#e0deff' }, textTransform: 'none', fontWeight: 600 }}
        >
          + Nuevo Alumno
        </Button>
        <Button
          variant="contained"
          size="small"
          onClick={() => navigate('/planifications/create')}
          sx={{ bgcolor: PRIMARY_LIGHT, color: PRIMARY, boxShadow: 'none', '&:hover': { bgcolor: '#e0deff' }, textTransform: 'none', fontWeight: 600 }}
        >
          + Planificación
        </Button>
        <Button
          variant="contained"
          size="small"
          onClick={() => navigate('/notas')}
          sx={{ bgcolor: PRIMARY_LIGHT, color: PRIMARY, boxShadow: 'none', '&:hover': { bgcolor: '#e0deff' }, textTransform: 'none', fontWeight: 600 }}
        >
          + Calificaciones
        </Button>
      </Stack>

      {/* KPI Cards */}
      <Grid container spacing={2} mb={3}>
        {KPI_CONFIGS.map((kpi) => (
          <Grid item xs={12} sm={6} md={3} key={kpi.key}>
            <Card
              elevation={0}
              sx={{ border: '1px solid #e1e1e7', borderRadius: 3, overflow: 'hidden', position: 'relative' }}
            >
              <Box sx={{ height: 4, bgcolor: kpi.color, position: 'absolute', top: 0, left: 0, right: 0 }} />
              <CardContent sx={{ pt: 3 }}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <Typography variant="caption" color="#6d6d77" fontWeight={500}>
                      {kpi.label}
                    </Typography>
                    <Typography variant="h4" fontWeight={700} color="#1c1c1e" lineHeight={1.2} mt={0.5}>
                      {kpi.getValue(data)}
                    </Typography>
                    <Typography variant="caption" color="#9e9ea6" mt={0.5} display="block">
                      {kpi.getSubtext(data)}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      width: 40, height: 40, borderRadius: 2.5, bgcolor: kpi.iconBg,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18,
                    }}
                  >
                    {kpi.icon}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Middle row */}
      <Grid container spacing={2} mb={2}>
        {/* Attendance by grade */}
        <Grid item xs={12} md={7}>
          <Card elevation={0} sx={{ border: '1px solid #e1e1e7', borderRadius: 3, height: '100%' }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                <Box>
                  <Typography fontWeight={600} color="#1c1c1e">Asistencia de Hoy</Typography>
                  <Typography variant="caption" color="#6d6d77">
                    Por grado — {data?.cycle?.name ?? 'Ciclo actual'}
                  </Typography>
                </Box>
                <Typography
                  variant="caption" fontWeight={600} color={PRIMARY} sx={{ cursor: 'pointer' }}
                  onClick={() => navigate('/attendance')}
                >
                  Ver detalles →
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              {(data?.attendanceByGrade ?? []).length === 0 ? (
                <Typography variant="body2" color="#9e9ea6" textAlign="center" py={3}>
                  Sin datos de asistencia hoy
                </Typography>
              ) : (
                (data?.attendanceByGrade ?? []).map((g) => {
                  const presentPct = g.total > 0 ? (g.present / g.total) * 100 : 0;
                  const latePct    = g.total > 0 ? (g.late / g.total) * 100 : 0;
                  const pct = g.total > 0 ? Math.round((g.present / g.total) * 100) : 0;
                  return (
                    <Box key={g.grade} mb={1.5}>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                        <Typography variant="body2" fontWeight={500} color="#1c1c1e" sx={{ minWidth: 100 }}>
                          {g.grade}
                        </Typography>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="caption" color="#6d6d77">{g.present}/{g.total || g.students}</Typography>
                          <Typography variant="caption" fontWeight={600} color="#f49e12">{pct}%</Typography>
                        </Box>
                      </Box>
                      <Box sx={{ position: 'relative', height: 8, bgcolor: '#f0f0f2', borderRadius: 1 }}>
                        <Box
                          sx={{
                            position: 'absolute', left: 0, top: 0, height: '100%',
                            width: `${presentPct}%`, bgcolor: '#22b14c', borderRadius: 1,
                          }}
                        />
                        <Box
                          sx={{
                            position: 'absolute', left: `${presentPct}%`, top: 0, height: '100%',
                            width: `${latePct}%`, bgcolor: '#f49e12',
                          }}
                        />
                      </Box>
                    </Box>
                  );
                })
              )}
              {(data?.attendanceByGrade ?? []).length > 0 && (
                <Box display="flex" gap={2} mt={1.5}>
                  {[['#22b14c', 'Presente'], ['#f49e12', 'Tardanza'], ['#ee4444', 'Ausente']].map(([color, label]) => (
                    <Box key={label} display="flex" alignItems="center" gap={0.5}>
                      <Box sx={{ width: 8, height: 8, borderRadius: 0.5, bgcolor: color }} />
                      <Typography variant="caption" color="#6d6d77">{label}</Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Students per grade */}
        <Grid item xs={12} md={5}>
          <Card elevation={0} sx={{ border: '1px solid #e1e1e7', borderRadius: 3, height: '100%' }}>
            <CardContent>
              <Typography fontWeight={600} color="#1c1c1e" mb={1}>Alumnos por Grado</Typography>
              <Divider sx={{ mb: 2 }} />
              {(data?.studentsPerGrade ?? []).length === 0 ? (
                <Typography variant="body2" color="#9e9ea6" textAlign="center" py={3}>
                  Sin datos
                </Typography>
              ) : (
                (data?.studentsPerGrade ?? []).map((g, i) => {
                  const pct = (g.count / maxStudents) * 100;
                  return (
                    <Box key={g.grade} mb={1.5}>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                        <Typography variant="body2" color="#1c1c1e" sx={{ minWidth: 100 }}>{g.grade}</Typography>
                        <Typography variant="body2" fontWeight={600} color="#1c1c1e">{g.count}</Typography>
                      </Box>
                      <Box sx={{ position: 'relative', height: 10, bgcolor: '#f0f0f2', borderRadius: 1.5 }}>
                        <Box
                          sx={{
                            position: 'absolute', left: 0, top: 0, height: '100%',
                            width: `${pct}%`, bgcolor: GRADE_COLORS[i % GRADE_COLORS.length], borderRadius: 1.5,
                          }}
                        />
                      </Box>
                    </Box>
                  );
                })
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Bottom row */}
      <Grid container spacing={2}>
        {/* Recent activity */}
        <Grid item xs={12} md={4}>
          <Card elevation={0} sx={{ border: '1px solid #e1e1e7', borderRadius: 3 }}>
            <CardContent>
              <Typography fontWeight={600} color="#1c1c1e" mb={1}>Actividad Reciente</Typography>
              <Divider sx={{ mb: 1.5 }} />
              {(data?.recentActivity ?? []).length === 0 ? (
                <Typography variant="body2" color="#9e9ea6" textAlign="center" py={2}>
                  Sin actividad reciente
                </Typography>
              ) : (
                (data?.recentActivity ?? []).map((a, i) => (
                  <Box key={i} display="flex" alignItems="flex-start" gap={1} mb={2}>
                    <Box
                      sx={{
                        width: 8, height: 8, borderRadius: 0.5, mt: 0.6, flexShrink: 0,
                        bgcolor: ACTIVITY_COLORS[a.type] ?? ACTIVITY_COLORS.default,
                      }}
                    />
                    <Box>
                      <Typography variant="body2" fontWeight={500} color="#1c1c1e">{a.message}</Typography>
                      <Typography variant="caption" color="#9e9ea6">{a.time}</Typography>
                    </Box>
                  </Box>
                ))
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Planifications this week */}
        <Grid item xs={12} md={5}>
          <Card elevation={0} sx={{ border: '1px solid #e1e1e7', borderRadius: 3 }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography fontWeight={600} color="#1c1c1e">Planificaciones esta semana</Typography>
                <Typography
                  variant="caption" fontWeight={600} color={PRIMARY} sx={{ cursor: 'pointer' }}
                  onClick={() => navigate('/planifications')}
                >
                  Ver todas →
                </Typography>
              </Box>
              <Divider sx={{ mb: 1 }} />
              {(data?.planifications ?? []).length === 0 ? (
                <Typography variant="body2" color="#9e9ea6" textAlign="center" py={2}>
                  Sin planificaciones esta semana
                </Typography>
              ) : (
                (data?.planifications ?? []).map((p, i) => {
                  const style = STATUS_STYLES[p.status] ?? STATUS_STYLES.pending;
                  const dayLabel = formatPlanDate(p.date);
                  const isToday = dayLabel === 'Hoy';
                  return (
                    <React.Fragment key={p.id}>
                      {i > 0 && <Divider sx={{ my: 1 }} />}
                      <Box display="flex" alignItems="center" gap={1.5}>
                        <Box
                          sx={{
                            minWidth: 48, height: 32, borderRadius: 2, display: 'flex',
                            alignItems: 'center', justifyContent: 'center',
                            bgcolor: isToday ? PRIMARY_LIGHT : 'transparent',
                            border: isToday ? 'none' : '1px solid #e1e1e7',
                          }}
                        >
                          <Typography variant="caption" fontWeight={600} color={isToday ? PRIMARY : '#6d6d77'}>
                            {dayLabel}
                          </Typography>
                        </Box>
                        <Box flex={1}>
                          <Typography variant="body2" fontWeight={600} color="#1c1c1e">{p.title}</Typography>
                          <Typography variant="caption" color="#9e9ea6">{p.curso}</Typography>
                        </Box>
                        <Chip
                          label={style.label}
                          size="small"
                          sx={{
                            bgcolor: style.bg, color: style.color, fontWeight: 500,
                            fontSize: 11, height: 24, borderRadius: 99,
                          }}
                        />
                      </Box>
                    </React.Fragment>
                  );
                })
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Cycle summary */}
        {data?.cycle && (
          <Grid item xs={12} md={3}>
            <Card elevation={0} sx={{ border: '1px solid #e1e1e7', borderRadius: 3, height: '100%' }}>
              <CardContent>
                <Typography fontWeight={600} color="#1c1c1e" mb={1}>{data.cycle.name}</Typography>
                <Divider sx={{ mb: 2 }} />
                {[
                  { value: data.cycle.activeCourses, label: 'Cursos activos', color: PRIMARY },
                  { value: data.cycle.teachers, label: 'Maestros', color: '#3b82f6' },
                  { value: data.cycle.totalDays, label: 'Días lectivos', color: '#22b14c' },
                  { value: data.cycle.remainingDays, label: 'Días restantes', color: '#f49e12' },
                ].map((item) => (
                  <Box key={item.label} mb={2}>
                    <Typography variant="h5" fontWeight={700} color={item.color}>{item.value}</Typography>
                    <Typography variant="caption" color="#6d6d77">{item.label}</Typography>
                  </Box>
                ))}
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </Box>
  );
}
